import { useEffect, useState } from "react";
import { Button, FormField } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { WITHDRAWAL_REASONS } from "@/api/dealLookups";
import type { DealDetailDto, DealListItemDto } from "@/types/deals";
import { useWithdrawDeal } from "@/hooks/deals/useDeals";
import { dealLabel } from "./dealFormat";

interface Props {
  open: boolean;
  deal: DealListItemDto | DealDetailDto | null;
  onClose: () => void;
  onWithdrawn?: (deal: DealDetailDto) => void;
}

const MIN_DETAIL = 10;
/** The server stores the detail in 2000-character columns and refuses a longer
 *  one (owner approval, 2026-10-13). */
const MAX_DETAIL = 2000;

/** Spec §5.7 — Withdraw modal. Same shape as Decline: a reason code and a
 *  required detail (here at least 10 characters). */
export function WithdrawModal({ open, deal, onClose, onWithdrawn }: Props) {
  const toast = useToast();
  const withdraw = useWithdrawDeal(deal?.id ?? "");
  const [reasonCode, setReasonCode] = useState("");
  const [detail, setDetail] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReasonCode("");
      setDetail("");
      setError(null);
    }
  }, [open]);

  if (!deal) return null;

  async function submit() {
    if (!reasonCode) {
      setError("Select a withdrawal reason.");
      return;
    }
    if (detail.trim().length < MIN_DETAIL) {
      setError(`Detail must be at least ${MIN_DETAIL} characters.`);
      return;
    }
    if (detail.trim().length > MAX_DETAIL) {
      setError(`Detail must be at most ${MAX_DETAIL} characters.`);
      return;
    }
    setError(null);
    try {
      const result = await withdraw.mutateAsync({
        reasonCode,
        detail: detail.trim(),
      });
      toast.success(`Withdrew ${dealLabel(deal!)}.`);
      onWithdrawn?.(result);
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError && err.errors.length
        ? err.errors.join("; ")
        : err instanceof Error
          ? err.message
          : "Withdrawal failed.";
      setError(msg);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Withdraw deal"
      description={`${deal.dealNumber} — ${dealLabel(deal)}`}
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={withdraw.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={submit} loading={withdraw.isPending}>
            Withdraw deal
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <p className="text-small text-gray-mid">
          The deal moves to <strong>Withdrawn</strong> with the reason captured for audit.
          It then becomes read-only: only an admin can edit it, though it can still be
          cloned, archived or restored.
        </p>

        <FormField label="Reason" required>
          <Select
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}
            aria-label="Withdrawal reason"
          >
            <option value="">Select a reason…</option>
            {WITHDRAWAL_REASONS.map((r) => (
              <option key={r.code} value={r.code}>
                {r.displayName}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Notes" required helperText={`${MIN_DETAIL}–${MAX_DETAIL} characters.`}>
          <Textarea
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            maxLength={MAX_DETAIL}
            rows={4}
            placeholder="Capture context for the withdrawal."
          />
        </FormField>

        {error && (
          <p className="text-small text-status-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
